'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import { FunnelStages, SortDirections, ColumnValuesToKey, getContactsStatus, ColumnValues, EntityTypes, ALL_NETWORKS, Networks } from 'AdsUI/lib/Constants';
import { getAccountNameForCampaign, getPaginatedData, getTypeForEntity } from 'AdsUI/lib/Utils';
import withQueryParams from 'AdsUI/lib/withQueryParams';
import { sortCollection } from 'AdsUI/lib/Sort';
import CampaignReportingTable from './CampaignReportingTable';
import EditColumnsContainer from 'AdsUI/containers/EditColumnsContainer';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import useFilterUpdate from 'AdsUI/lib/useFilterUpdate';
import getTrackingStatus, { TRACKING_STATUS } from 'AdsUI/lib/trackingStatus';
import { useSelector } from 'react-redux';
import { isFreeTier } from 'AdsUI/selectors/AuthSelectors';

const nameFilter = searchQuery => ({
  entity
}) => {
  if (!searchQuery) {
    return true;
  } // AdWords ads don't have names


  const name = entity.getSearchString ? entity.getSearchString() : (entity.name || entity.id.toString()).toLowerCase();
  return name.indexOf(searchQuery) > -1;
};

const nameSort = (adAccounts, sortDir) => (a, b) => {
  const aName = getAccountNameForCampaign(a, adAccounts);
  const bName = getAccountNameForCampaign(b, adAccounts);

  if (aName > bName) {
    return 1 * sortDir;
  } else if (bName > aName) {
    return -1 * sortDir;
  }

  return 0;
};

const FilteredAndSortedCampaignReportingTable = (_ref) => {
  let {
    campaignChildren,
    campaignEntity,
    query,
    loading,
    location,
    history,
    adAccounts,
    entityType = EntityTypes.CAMPAIGN,
    parent,
    statusFilter,
    setQueryParam,
    globalFilters,
    sequenceStep,
    match: {
      params
    },
    allWorkflows
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["campaignChildren", "campaignEntity", "query", "loading", "location", "history", "adAccounts", "entityType", "parent", "statusFilter", "setQueryParam", "globalFilters", "sequenceStep", "match", "allWorkflows"]);

  const searchQuery = query.get('search') || '';
  const page = parseInt(query.get('page'), 10) || 1;
  const network = parent ? parent.network : ALL_NETWORKS;
  const {
    showEditColumns
  } = new URLSearchParams(location.search);
  const [{
    sortKey,
    direction
  }, setSort] = useState({
    sortKey: FunnelStages.CONVERSIONS,
    sortDirection: SortDirections.DESCENDING
  });
  const [editColumnsModalOpen, onChangeEditColumnsModalStatus] = useState(false);
  useEffect(() => {
    if (showEditColumns) {
      onChangeEditColumnsModalStatus(true);
    }
  }, [showEditColumns]);
  const onOpenEditColumnsModal = useCallback(() => onChangeEditColumnsModalStatus(true), [onChangeEditColumnsModalStatus]);
  const onCloseEditColumnsModal = useCallback(() => {
    onChangeEditColumnsModalStatus(false);

    if (showEditColumns) {
      history.replace(location);
    }
  }, [onChangeEditColumnsModalStatus, history, showEditColumns, location]);
  const inEarlyRequest = useMemo(() => campaignChildren && campaignChildren.size > 0 && !campaignChildren.some(child => child.report), [campaignChildren]);
  const sortedAndFilteredChildren = useMemo(() => {
    if (loading || !campaignChildren) {
      return null;
    }

    const sortPath = ColumnValuesToKey[sortKey]; // TODO: If you want to get rid of this status filter, make sure that
    // CampaignReportingReducer will properly remove children that are the
    // wrong status (as CampaignListReducer already does)

    const filteredChildren = campaignChildren.filter(nameFilter(searchQuery.toLowerCase())).filter(child => !statusFilter || statusFilter.has(getContactsStatus(child.entity.status))); // We're partially loaded (entities only)

    if (inEarlyRequest) {
      return filteredChildren;
    }

    if (sortKey === ColumnValues.NAME) {
      return sortCollection(filteredChildren.toList(), ['entity', 'name'], direction);
    } else if (sortKey === ColumnValues.AD_ACCOUNT) {
      return filteredChildren.sort(nameSort(adAccounts, direction));
    } else if (sortKey === ColumnValues.TYPE && entityType === EntityTypes.ADGROUP && network === Networks.LINKEDIN) {
      const sortedChildren = filteredChildren.toList().sortBy(child => getTypeForEntity(child.entity, entityType, campaignEntity));
      return direction === SortDirections.ASCENDING ? sortedChildren : sortedChildren.reverse();
    } else {
      const amountSortPath = sortPath && filteredChildren.first() && filteredChildren.first().hasIn([...sortPath, 'amount']) ? [...sortPath, 'amount'] : sortPath;
      return sortCollection(filteredChildren.toList(), amountSortPath, direction);
    }
  }, [loading, campaignChildren, searchQuery, direction, sortKey, statusFilter, adAccounts, entityType, network, campaignEntity, inEarlyRequest]);
  const [paginatedChildren, pageCount] = getPaginatedData(sortedAndFilteredChildren, page);
  const onPageChange = useCallback(e => setQueryParam('page', e.target.value, true), [setQueryParam]);
  const onSearchChange = useCallback(e => {
    setQueryParam('search', e.target.value, false, ['page']);
  }, [setQueryParam]);
  const onSortChange = useCallback(sortState => {
    setQueryParam('page', 1, true);
    setSort(sortState);
  }, [setQueryParam, setSort]);
  const {
    totals
  } = props;
  useEffect(() => {
    if (!totals || !totals.newContacts) {
      return;
    }

    usageTracker.track('viewedConversions', {
      conversions: totals.newContacts
    });
  }, [totals]);
  useFilterUpdate(globalFilters, useCallback(() => {
    if (page > 1) {
      setQueryParam('page', 1, true);
    }
  }, [setQueryParam, page]));
  const allTrackingErrors = useMemo(() => {
    const children = sortedAndFilteredChildren || [];
    const trackingErrors = children.map(({
      entity,
      trackingStatus
    }) => {
      const adAccount = adAccounts.find(account => entity.accountId === account.accountId && entity.network === account.network);

      if (!trackingStatus || !adAccount) {
        return null;
      }

      const adGroupEntity = entityType === EntityTypes.AD ? parent : entityType === EntityTypes.ADGROUP ? entity : null;
      const adEntity = entityType === EntityTypes.AD ? entity : null;
      const status = getTrackingStatus(adAccount, campaignEntity || entity, adGroupEntity, adEntity, trackingStatus, globalFilters.timeRange);

      if (status !== TRACKING_STATUS.ERROR && status !== TRACKING_STATUS.DISMISSED) {
        return null;
      }

      const dismissedCount = trackingStatus.statuses.filter(statusInfo => statusInfo.dismissedAt).size;
      return {
        campaignId: (campaignEntity || entity).id,
        adAccount,
        count: trackingStatus.statuses.size - dismissedCount
      };
    }).filter(Boolean);
    return trackingErrors;
  }, [sortedAndFilteredChildren, adAccounts, campaignEntity, entityType, parent, globalFilters]);
  const mappedEntityType = useMemo(() => {
    if (sequenceStep) {
      return sequenceStep;
    }

    return entityType;
  }, [entityType, sequenceStep]);
  const freeTier = useSelector(isFreeTier);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(CampaignReportingTable, Object.assign({
      campaignChildren: paginatedChildren,
      campaignEntity: campaignEntity,
      loading: loading,
      adAccounts: adAccounts,
      parent: parent,
      sortDirection: direction,
      sortKey: sortKey,
      entityType: entityType,
      globalFilters: globalFilters,
      params: {
        match: {
          params
        }
      },
      allTrackingErrors: allTrackingErrors,
      location: location,
      history: history,
      inEarlyRequest: inEarlyRequest
    }, props, {
      searchQuery: searchQuery,
      onSearchChange: onSearchChange,
      onSortChange: onSortChange,
      pageCount: pageCount,
      page: Math.min(page, pageCount),
      onPageChange: onPageChange,
      onOpenEditColumnsModal: onOpenEditColumnsModal,
      sequenceStep: sequenceStep,
      allWorkflows: allWorkflows,
      freeTier: freeTier
    })), editColumnsModalOpen && /*#__PURE__*/_jsx(EditColumnsContainer, {
      network: network,
      entityType: mappedEntityType,
      onClose: onCloseEditColumnsModal
    })]
  });
};

export default withQueryParams(FilteredAndSortedCampaignReportingTable);